// @ts-nocheck
/* eslint-disable */
import React from 'react';
import styled from 'styled-components';
import { Text } from '@oneaudi/unified-web-components';
import {
  breakpointsMap,
  SYS_SPACE_FIXED_300,
  SYS_SPACE_FIXED_350,
  SYS_SPACE_FIXED_400,
} from '@oneaudi/unified-web-common';
import { useI18n } from '@oneaudi/i18n-context';
import config from '../../../config/config.json';
import { messages } from '../../../i18n/messages';
import { PlacesList } from './PlacesList';
import { DealersList } from './DealersList';
import { useKeyboardNavigation } from './useKeyboardNavigation';
import { useGooglePlaces } from './useGooglePlaces';
import useSearchByTerm from '../../../hooks/useSearchByTerm';
import type { Dealer } from '../../../models/dealer';
import type { City } from '../../../models/common/places';
import { getActiveFilters } from '../../../utils/activeFilters';
import mapContext from '../../../contexts/map-context';
import { generateBaseUrl } from '../../../utils/generateBaseUrl';
import type { UnifiedTheme } from '../../../FeatureApp';

export type Props = {
  isOnMap: boolean;
  term: string;
  countryCode: string;
  intlSearchEnabled: boolean;
  isGoogleMapsApiLoaded: boolean;

  onPlaceSelect: (city: City, pos?: number) => void;

  onDealerSelect: (dealer: Dealer, pos?: number) => void;

  onKeyboardSelect: (name: string) => void;
  onClose: () => void;
  showPartnerSuggestions: boolean;
};

export const Autocompletion = ({
  term,
  isGoogleMapsApiLoaded,
  intlSearchEnabled,
  countryCode,
  isOnMap,
  onPlaceSelect,
  onDealerSelect,
  onKeyboardSelect,
  onClose,
  showPartnerSuggestions,
}: Props) => {
  const [activeTerm, setActiveTerm] = React.useState(term);
  const [selectedName, setSelectedName] = React.useState('');
  const containerRef = React.useRef<HTMLUListElement>(null);
  const places = useGooglePlaces(
    isGoogleMapsApiLoaded,
    activeTerm,
    intlSearchEnabled ? undefined : countryCode,
  );

  const order: '3' = '3';

  const { userSelectedFilters, preselectedFilters } = mapContext.useMapState();

  const activeFilters = getActiveFilters(preselectedFilters, userSelectedFilters);

  const { loading, dealersResult } = useSearchByTerm(
    activeTerm,
    intlSearchEnabled ? undefined : countryCode,
    activeFilters,
  );

  const dealersAmount = dealersResult?.length ?? 0;

  const submitForGeolocationSearch = (idx: number) => {
    if (isOnMap && idx < places.length && idx >= 0 && places[idx]) {
      onPlaceSelect(places[idx]);
    } else if (
      isOnMap &&
      idx >= places.length &&
      dealersResult &&
      dealersResult[idx - places.length]
    ) {
      onDealerSelect(dealersResult[idx - places.length]);
    }
  };

  const selectedPosition = useKeyboardNavigation(places.length + dealersAmount, isOnMap, {
    onEscape: () => {
      onKeyboardSelect(activeTerm);
      onClose();
    },
    onEnter: onClose,

    onTab: () => {},
    onEnterDown: submitForGeolocationSearch,
  });

  const getSelectedName = () =>
    selectedPosition < places.length
      ? places[selectedPosition].name
      : dealersResult?.length
        ? dealersResult[selectedPosition - places.length].name
        : '';

  React.useEffect(() => {
    if (selectedPosition > -1) {
      const name = getSelectedName();
      setSelectedName(name);
      onKeyboardSelect(name);
    }
  }, [selectedPosition, places.length]);

  React.useEffect(() => {
    if (term !== selectedName) {
      setActiveTerm(term);
    }
  }, [term]);

  const placesText = useI18n(messages.autocompletion_places);
  const audiPartnerText = useI18n(messages.autocompletion_audi_partner);
  const placeNotFound = useI18n(messages.place_not_found);

  const isCompact =
    (containerRef.current?.offsetWidth || 0) <
    parseInt(breakpointsMap.sm, 10) + parseInt(SYS_SPACE_FIXED_300, 10) * 2;
  const baseUrl = generateBaseUrl();

  return (
    <>
      <AutocompletionContainer
        ref={containerRef}
        role="listbox"
        id="search-input-options"
        data-test-id="autocompletion-component"
        aria-label="search-input-options"
      >
        <Section
          data-test-id="places-section"
          aria-labelledby={`${config.searchInputId}-options`}
          role="listitem"
        >
          {places.length === 0 && dealersAmount === 0 && !loading ? (
            <SectionHeader>
              <SectionTitle
                tag="span"
                variant={{ order: '2', type: 'copy', style: 'normal' }}
                data-test-id="place-not-found-title"
              >
                {placeNotFound}
              </SectionTitle>
            </SectionHeader>
          ) : (
            <>
              <SectionHeader>
                <SectionTitle
                  tag="span"
                  variant={{ order, type: 'copy', style: 'normal' }}
                  data-test-id="section-title"
                >
                  {placesText}
                </SectionTitle>
                {isCompact && (
                  <img
                    src={`${baseUrl}/powered_by_google_on_white_hdpi.png`}
                    alt="powered by Google"
                    width="117"
                    height="16"
                    data-test-id="google-logo"
                  />
                )}
              </SectionHeader>
              <PlacesList
                places={places}
                query={activeTerm}
                selectedPosition={selectedPosition < places.length ? selectedPosition : undefined}
                onPlaceClick={onPlaceSelect}
                data-test-id="component-places-list"
              />
            </>
          )}
        </Section>
        {showPartnerSuggestions && Boolean(loading || dealersAmount) && (
          <Section
            data-test-id="dealers-section"
            aria-labelledby={`${config.searchInputId}-options`}
            role="listitem"
          >
            <SectionHeader>
              <SectionTitle
                isLegacy={process.env.IS_LEGACY}
                tag="span"
                variant={{ order, type: 'copy', style: 'normal' }}
                data-test-id="section-title"
              >
                {audiPartnerText}
              </SectionTitle>
            </SectionHeader>
            <DealersList
              isLoading={loading}
              isCompact={isCompact}
              dealers={dealersResult || []}
              selectedPosition={
                selectedPosition >= places.length ? selectedPosition - places.length : undefined
              }
              onDealerClick={onDealerSelect}
              data-test-id="component-dealers-list"
            />
          </Section>
        )}
      </AutocompletionContainer>
      <div id="search-input-options" style={{ display: 'none' }} aria-hidden="true" />
    </>
  );
};

export default Autocompletion;

const AutocompletionContainer = styled.ul`
  background-color: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  list-style: none;
  padding: 0;
  margin: 0 ${SYS_SPACE_FIXED_350};
`;

const Section = styled.li`
  margin: 0 0 ${SYS_SPACE_FIXED_400};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled(Text)<{ isLegacy?: boolean }>`
  color: ${(props) => props.isLegacy && '#666666'};
`;
